import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CreditCard, Money, Tag, CheckCircle } from '@phosphor-icons/react';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user, fetchCartCount } = useContext(AuthContext);
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    name: user?.name || '', address: '', city: '', state: '', zipCode: '', country: 'India'
  });

  useEffect(() => { fetchCart(); }, []);

  const fetchCart = async () => {
    try {
      const response = await apiClient.get('/cart');
      if (response.data.items.length === 0) { toast.error('Your cart is empty'); navigate('/cart'); return; }
      setCart(response.data);
    } catch (error) { console.error('Error fetching cart:', error); }
    finally { setLoading(false); }
  };

  const calculateTotal = () => cart.items.reduce((t, item) => t + (item.product?.base_price || 0) * item.quantity, 0);
  const calculateFinal = () => calculateTotal() - discount;

  const applyCoupon = async () => {
    if (!couponCode.trim()) { toast.error('Enter a coupon code'); return; }
    try {
      const response = await apiClient.post(`/coupons/validate?code=${couponCode}`);
      const discountAmt = calculateTotal() * (response.data.discount_percent / 100);
      setDiscount(discountAmt);
      setCouponApplied(true);
      toast.success(`Coupon applied! ${response.data.discount_percent}% off`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setDiscount(0); setCouponApplied(false);
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      const orderItems = cart.items.map(item => ({
        product_id: item.product_id, product_name: item.product?.name,
        customization_id: item.customization_id, quantity: item.quantity,
        size: item.size, price: item.product?.base_price
      }));

      if (paymentMethod === 'cod') {
        const orderResponse = await apiClient.post('/orders/cod', {
          items: orderItems, total_amount: calculateTotal(),
          shipping_address: shippingAddress, payment_method: 'cod',
          coupon_code: couponApplied ? couponCode : null
        });
        fetchCartCount();
        toast.success('Order placed! Cash on Delivery');
        navigate(`/orders`);
      } else {
        const orderResponse = await apiClient.post('/orders', {
          items: orderItems, total_amount: calculateTotal(),
          shipping_address: shippingAddress, payment_method: 'online',
          coupon_code: couponApplied ? couponCode : null
        });
        const originUrl = window.location.origin;
        const checkoutResponse = await apiClient.post('/checkout/session', {
          order_id: orderResponse.data.id, origin_url: originUrl
        });
        await apiClient.delete('/cart');
        fetchCartCount();
        window.location.href = checkoutResponse.data.url;
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      toast.error('Checkout failed. Please try again.');
      setProcessing(false);
    }
  };

  if (loading) return <div className="min-h-screen pt-24 flex items-center justify-center"><p className="text-slate-400">Loading checkout...</p></div>;

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto px-8 md:px-16 lg:px-24">
        <h1 className="text-4xl sm:text-5xl font-medium mb-8 text-foreground" data-testid="checkout-heading">Checkout</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Payment Method Selection */}
            <div className="bg-card border border-border p-8" data-testid="payment-method-section">
              <h2 className="text-xl font-heading font-normal mb-6 text-foreground">Payment Method</h2>
              <div className="grid grid-cols-2 gap-4">
                <button onClick={() => setPaymentMethod('online')}
                  className={`p-6 border text-left transition-all ${paymentMethod === 'online' ? 'border-primary bg-[#D4AF37]/10' : 'border-border hover:border-primary/30'}`}
                  data-testid="payment-online-btn">
                  <CreditCard size={28} weight="regular" className={paymentMethod === 'online' ? 'text-[#D4AF37]' : 'text-slate-400'} />
                  <p className="text-sm font-medium text-foreground mt-2">Pay Online</p>
                  <p className="text-xs text-slate-400 mt-1">Card / UPI / Netbanking</p>
                </button>
                <button onClick={() => setPaymentMethod('cod')}
                  className={`p-6 border text-left transition-all ${paymentMethod === 'cod' ? 'border-primary bg-[#D4AF37]/10' : 'border-border hover:border-primary/30'}`}
                  data-testid="payment-cod-btn">
                  <Money size={28} weight="regular" className={paymentMethod === 'cod' ? 'text-[#D4AF37]' : 'text-slate-400'} />
                  <p className="text-sm font-medium text-foreground mt-2">Cash on Delivery</p>
                  <p className="text-xs text-slate-400 mt-1">Pay when you receive</p>
                </button>
              </div>
            </div>

            {/* Shipping Form */}
            <form onSubmit={handleCheckout} className="bg-card border border-border p-8" data-testid="checkout-form">
              <h2 className="text-xl font-heading font-normal mb-6 text-foreground">Shipping Information</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input id="name" value={shippingAddress.name} onChange={(e) => setShippingAddress({...shippingAddress, name: e.target.value})} required data-testid="shipping-name-input" />
                </div>
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input id="address" value={shippingAddress.address} onChange={(e) => setShippingAddress({...shippingAddress, address: e.target.value})} required data-testid="shipping-address-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label htmlFor="city">City</Label><Input id="city" value={shippingAddress.city} onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})} required data-testid="shipping-city-input" /></div>
                  <div><Label htmlFor="state">State</Label><Input id="state" value={shippingAddress.state} onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})} required data-testid="shipping-state-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label htmlFor="zipCode">Zip Code</Label><Input id="zipCode" value={shippingAddress.zipCode} onChange={(e) => setShippingAddress({...shippingAddress, zipCode: e.target.value})} required data-testid="shipping-zip-input" /></div>
                  <div><Label htmlFor="country">Country</Label><Input id="country" value={shippingAddress.country} onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value})} required data-testid="shipping-country-input" /></div>
                </div>
              </div>
              <Button type="submit" className="w-full mt-8 rounded-sm bg-[#D4AF37] hover:bg-[#D4AF37]/90" disabled={processing} data-testid="place-order-button">
                {processing ? 'Processing...' : paymentMethod === 'cod' ? 'Place Order (Cash on Delivery)' : 'Proceed to Payment'}
              </Button>
            </form>
          </div>

          {/* Order Summary + Coupon */}
          <div className="lg:col-span-1 space-y-4">
            {/* Coupon Code */}
            <div className="bg-card border border-border p-6" data-testid="coupon-section">
              <h3 className="text-lg font-heading font-normal mb-4 text-foreground">Have a Coupon?</h3>
              <div className="flex gap-2">
                <Input placeholder="Enter code" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} disabled={couponApplied} data-testid="coupon-input" />
                <Button onClick={applyCoupon} variant="outline" className="rounded-sm" disabled={couponApplied} data-testid="apply-coupon-btn">
                  {couponApplied ? <CheckCircle size={18} className="text-green-500" /> : <Tag size={18} />}
                </Button>
              </div>
              {couponApplied && <p className="text-xs text-green-400 mt-2">Coupon applied! You save ${discount.toFixed(2)}</p>}
            </div>

            {/* Summary */}
            <div className="bg-card border border-border p-6 sticky top-24" data-testid="checkout-summary">
              <h2 className="text-xl font-heading font-normal mb-6 text-foreground">Order Summary</h2>
              <div className="space-y-4 mb-6">
                {cart.items.map((item, index) => (
                  <div key={item.id} className="flex justify-between text-sm" data-testid={`summary-item-${index}`}>
                    <span className="text-slate-300">{item.product?.name} x {item.quantity}</span>
                    <span className="text-slate-300">${((item.product?.base_price || 0) * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-2 border-t border-border pt-4">
                <div className="flex justify-between text-sm text-slate-400">
                  <span>Subtotal</span><span>${calculateTotal().toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm text-green-400" data-testid="discount-line">
                    <span>Discount</span><span>-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-slate-400">
                  <span>Payment</span><span>{paymentMethod === 'cod' ? 'Cash on Delivery' : 'Online Payment'}</span>
                </div>
                <div className="flex justify-between text-lg font-medium pt-2 border-t border-border" data-testid="checkout-total">
                  <span className="text-foreground">Total</span>
                  <span className="text-[#D4AF37]">${calculateFinal().toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
